import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Calendar,
    Clock,
    User,
    MapPin,
    Video,
    ChevronLeft,
    ChevronDown,
    ChevronUp,
    CheckCircle,
    XCircle,
    Edit,
    Trash2,
    Send,
    Loader2,
    MessageSquare,
    Info,
    CalendarCheck,
    AlertCircle,
    Briefcase,
    Layers,
    ExternalLink,
    Link as LinkIcon
} from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import interviewService from '../../../api/services/interviewService';
import Button from '../../../components/common/Button/Button';
import PermissionGate from '../../../components/common/PermissionGate';
import { formatDate, formatTime } from '../../../utils/stringUtils';
import { useAuth } from '../../../context/AuthContext';
import ScheduleInterviewModal from '../../../components/recruitment/ScheduleInterviewModal';

const ViewInterview = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const queryClient = useQueryClient();
    const [interview, setInterview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showScheduleForm, setShowScheduleForm] = useState(false);
    const [editInterviewData, setEditInterviewData] = useState(null);

    const handleEditRound = (round) => {
        const finalEditData = {
            ...interview,
            roundType: round.roundType,
            interviewer: round.interviewer,
            interviewerId: round.interviewer?.id || round.interviewer?._id,
            interviewerName: round.interviewer?.name,
            interviewerEmail: round.interviewer?.email,
            scheduledAt: round.scheduledAt,
            duration: round.duration,
            type: round.type || round.interviewType,
            location: round.location,
            meetingLink: round.meetingLink,
            notes: round.notes || round.extraNote,
            showInterviewerToCandidate: round.showInterviewerToCandidate !== undefined ? round.showInterviewerToCandidate : interview?.showInterviewerToCandidate,
        };
        setEditInterviewData(finalEditData);
        setShowScheduleForm(true);
    };

    // Per-round assessment states
    const [actingRoundId, setActingRoundId] = useState(null);
    const [pendingStatus, setPendingStatus] = useState(null);
    const [internalNotes, setInternalNotes] = useState('');
    const [expandedRounds, setExpandedRounds] = useState({});

    const toggleRound = (roundId) => {
        setExpandedRounds(prev => ({
            ...prev,
            [roundId]: !prev[roundId]
        }));
    };

    useEffect(() => {
        if (interview?.rounds?.length > 0) {
            // Expand first round by default if none specified
            if (Object.keys(expandedRounds).length === 0) {
                setExpandedRounds({ [interview.rounds[0].id || 0]: true });
            }
        }
    }, [interview]);

    const getTypeIcon = (type) => {
        switch (type) {
            case 'video': return Video;
            case 'in_person': return MapPin;
            default: return Info;
        }
    };

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'scheduled': return 'bg-blue-100 text-blue-800 border-blue-200';
            case 'rescheduled': return 'bg-amber-100 text-amber-800 border-amber-200';
            case 'completed': return 'bg-green-100 text-green-800 border-green-200';
            case 'cancelled': return 'bg-red-100 text-red-800 border-red-200';
            case 'in progress': return 'bg-purple-100 text-purple-800 border-purple-200';
            default: return 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    useEffect(() => {
        const fetchInterview = async () => {
            try {
                setLoading(true);
                const response = await interviewService.getInterviewById(id);
                if (response.responseCode === 200 && response.data) {
                    setInterview(response.data);
                } else {
                    toast.error(response.responseMessage || 'Failed to fetch interview details');
                    navigate('/interview_scheduling');
                }
            } catch (error) {
                toast.error('An error occurred while fetching details');
                navigate('/interview_scheduling');
            } finally {
                setLoading(false);
            }
        };

        if (id) fetchInterview();
    }, [id, navigate]);

    const updateStatusMutation = useMutation({
        mutationFn: ({ status, additionalData }) => interviewService.updateInterviewStatus(id, status, additionalData),
        onSuccess: (data) => {
            if (data.success || data.responseCode === 200) {
                toast.success('Status updated successfully');
                setInterview(prev => ({ ...prev, ...data.data }));
                setInternalNotes('');
                setActingRoundId(null);
                setPendingStatus(null);
                queryClient.invalidateQueries(['interviews']);
            } else {
                toast.error(data.responseMessage || 'Failed to update status');
            }
        },
        onError: (error) => {
            const errorMessage = error.response?.data?.responseMessage || error.message || 'Failed to update status';
            toast.error(errorMessage);
        }
    });

    const sendReminderMutation = useMutation({
        mutationFn: (id) => interviewService.sendInterviewReminder(id),
        onSuccess: (data) => {
            if (data.responseCode === 200) {
                toast.success('Reminder sent successfully');
            } else {
                toast.error(data.responseMessage || 'Failed to send reminder');
            }
        }
    });

    const handleDelete = async () => {
        if (window.confirm('Are you sure you want to delete this interview?')) {
            try {
                const response = await interviewService.deleteInterview(id);
                if (response.responseCode === 200) {
                    toast.success('Interview deleted successfully');
                    navigate('/interview_scheduling');
                } else {
                    toast.error(response.responseMessage || 'Failed to delete interview');
                }
            } catch (error) {
                toast.error('Failed to delete interview');
            }
        }
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh]">
                <Loader2 className="h-10 w-10 text-brand animate-spin mb-4" />
                <p className="text-gray-500 font-medium">Fetching interview details...</p>
            </div>
        );
    }

    if (!interview) return null;

    const canModify = user?.role === 'hr_manager' || user?.role === 'super_admin' || user?.role === 'admin';

    return (
        <div className="w-full space-y-6 pb-12 animate-in fade-in duration-500">
            {/* Header / Navigation */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate('/interview_scheduling')}
                        className="w-10 h-10 flex items-center justify-center hover:bg-white rounded-full transition-all border border-gray-200 shadow-sm group"
                    >
                        <ChevronLeft className="h-5 w-5 text-gray-600 group-hover:text-brand" />
                    </button>
                    <div>
                        <h1 className="text-xl font-bold text-gray-900 leading-tight">
                            Interview Assessment
                        </h1>
                        <p className="text-gray-500 text-xs font-medium">
                            {interview.interviewId} &nbsp;·&nbsp; {interview.candidate?.name}
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    {['Scheduled', 'Rescheduled'].includes(interview.status) && (
                        <Button
                            variant="outline"
                            onClick={() => sendReminderMutation.mutate(interview.id)}
                            icon={sendReminderMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                            className="bg-white border-gray-200 text-xs font-bold"
                        >
                            Send Reminder
                        </Button>
                    )}


                    <PermissionGate permission="recruitment" action="delete">
                        <Button
                            variant="danger"
                            onClick={handleDelete}
                            icon={<Trash2 className="h-4 w-4" />}
                            className="bg-red-50 text-red-600 border-red-100 text-xs font-bold"
                        >
                            Delete
                        </Button>
                    </PermissionGate>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left Column: Round Details & Participants */}
                <div className="lg:col-span-2 space-y-6">

                    {/* Schedule Information Card */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                        <div className="bg-gray-50 px-5 py-3 border-b border-gray-100 flex items-center justify-between">
                            <div className="flex items-center gap-2 text-sm font-bold text-gray-800">
                                <CalendarCheck className="h-4 w-4 text-brand" />
                                <span>Schedule information</span>
                            </div>
                            <span className={`px-3 py-0.5 rounded-full text-[10px] font-bold border uppercase tracking-wider ${getStatusColor(interview.status)}`}>
                                {interview.status}
                            </span>
                        </div>
                        <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="space-y-1">
                                <span className="text-[10px] text-gray-400 uppercase font-bold tracking-widest">Date</span>
                                <div className="flex items-center gap-2 text-gray-900 font-semibold text-sm">
                                    <Calendar className="h-4 w-4 text-brand/60" />
                                    {formatDate(interview.scheduledAt, { includeTime: false })}
                                </div>
                            </div>
                            <div className="space-y-1">
                                <span className="text-[10px] text-gray-400 uppercase font-bold tracking-widest">Time & Duration</span>
                                <div className="flex items-center gap-2 text-gray-900 font-semibold text-sm">
                                    <Clock className="h-4 w-4 text-brand/60" />
                                    {formatTime(interview.scheduledAt)} ({interview.duration || 60} mins)
                                </div>
                            </div>
                            <div className="space-y-1">
                                <span className="text-[10px] text-gray-400 uppercase font-bold tracking-widest">Interview type</span>
                                <div className="flex items-center gap-2 text-gray-900 font-semibold text-sm capitalize">
                                    {React.createElement(getTypeIcon(interview.type), { className: "h-4 w-4 text-brand/60" })}
                                    {interview.type?.replace('_', ' ')}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Participants Card */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                        <div className="bg-gray-50 px-5 py-3 border-b border-gray-100 flex items-center gap-2 text-sm font-bold text-gray-800">
                            <User className="h-4 w-4 text-brand" />
                            <span>Participants</span>
                        </div>
                        <div className="p-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div>
                                    <span className="text-[10px] text-gray-400 uppercase font-bold tracking-widest block mb-3">Candidate</span>
                                    <div className="flex items-start gap-3">
                                        <div className="w-11 h-11 bg-blue-50 text-[#0F2942] rounded-full flex items-center justify-center font-bold text-sm border border-blue-100 flex-shrink-0">
                                            {interview.candidate?.name?.split(' ').map(n => n[0]).join('').toUpperCase()}
                                        </div>
                                        <div className="space-y-0.5">
                                            <p className="font-bold text-gray-950 text-sm capitalize">{interview.candidate?.name}</p>
                                            <p className="text-gray-500 text-xs font-medium">{interview.candidate?.email}</p>
                                            <p className="text-gray-500 text-xs font-medium">{interview.candidate?.phone}</p>
                                            <span className="inline-block mt-2 px-2 py-0.5 bg-gray-100 text-gray-600 text-[10px] font-bold rounded border border-gray-200">
                                                {interview.job?.department || "MERN"}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <span className="text-[10px] text-gray-400 uppercase font-bold tracking-widest block mb-3">Interviewers / Panel</span>
                                    <div className="space-y-3">
                                        {(interview.interviewers && interview.interviewers.length > 0 ? interview.interviewers : (interview.interviewer ? [interview.interviewer] : [])).map((int, idx) => (
                                            <div key={int.id || idx} className="flex items-start gap-3">
                                                <div className="w-9 h-9 bg-purple-50 text-purple-600 rounded-full flex items-center justify-center font-bold text-xs border border-purple-100 flex-shrink-0">
                                                    {int.name?.split(' ').map(n => n[0]).join('').toUpperCase() || "INT"}
                                                </div>
                                                <div className="space-y-0.5 min-w-0">
                                                    <p className="font-bold text-gray-950 text-sm capitalize truncate">{int.name}</p>
                                                    <p className="text-gray-500 text-xs font-medium truncate">{int.email}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <div className="mt-4 pt-4 border-t border-gray-50">
                                <span className="text-[10px] text-gray-400 uppercase font-bold tracking-widest block mb-1">Job position</span>
                                <div className="flex items-center gap-2">
                                    <span className="text-sm font-bold text-gray-900">{interview.job?.title}</span>
                                    <span className="bg-blue-50 text-[#0B2033] text-[10px] font-bold px-2 py-0.5 rounded border border-blue-100">
                                        {interview.job?.department || "Internal"} Dept.
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Interview Rounds List */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                        <div className="bg-gray-50 px-5 py-3 border-b border-gray-100 flex items-center justify-between">
                            <div className="flex items-center gap-2 text-sm font-bold text-gray-800">
                                <Layers className="h-4 w-4 text-brand" />
                                <span>Interview rounds</span>
                                <span className="text-xs font-normal text-gray-500 font-sans ml-1 uppercase tracking-tighter">({interview.rounds?.length || 0} rounds)</span>
                            </div>
                        </div>
                        <div className="p-5 space-y-4">
                            {interview.rounds?.map((round, index) => {
                                const isInterviewer = (user?.id || user?._id) === (round.interviewer?.id || round.interviewer?._id || round.interviewer);
                                const isCreator = (user?.id || user?._id) === (interview.createdBy?.id || interview.createdBy?._id || interview.createdBy);
                                const isSuperAdmin = user?.role === 'super_admin';
                                const canHandleRound = isInterviewer || isCreator || isSuperAdmin;

                                 const isExpanded = expandedRounds[round.id || index];

                                 return (
                                 <div key={round.id || index} className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm hover:shadow-md hover:border-brand/30 transition-all duration-200">
                                     <div 
                                        className="bg-gray-50/50 px-4 py-3 flex items-center justify-between border-b border-gray-100 cursor-pointer hover:bg-gray-100/50 transition-all group/header"
                                        onClick={() => toggleRound(round.id || index)}
                                     >
                                         <div className="flex items-center gap-3">
                                            <div className="w-6 h-6 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-400 group-hover/header:border-brand group-hover/header:text-brand transition-all">
                                                {isExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                                            </div>
                                            <div>
                                                <div className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">Round {index + 1}</div>
                                                <div className="text-sm font-bold text-gray-900 capitalize">{round.roundType}</div>
                                            </div>
                                         </div>
                                         <div className="flex items-center gap-2">
                                             <PermissionGate permission="recruitment" action="update">
                                                 {!['completed', 'cancelled'].includes(round.status?.toLowerCase()) && canHandleRound && (
                                                     <button
                                                         onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleEditRound(round);
                                                         }}
                                                         className="p-1 px-2 border border-blue-200 text-[#0B2033] bg-blue-50/50 rounded-md text-[9px] font-bold uppercase tracking-tight hover:bg-[#0F2942] hover:text-white hover:border-[#0F2942] transition-all flex items-center gap-1"
                                                         title="Edit Round"
                                                     >
                                                         <Edit className="h-2.5 w-2.5" /> Edit
                                                     </button>
                                                 )}
                                             </PermissionGate>
                                             <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border uppercase tracking-wider ${getStatusColor(round.status)}`}>
                                                 {round.status}
                                             </span>
                                         </div>
                                     </div>
                                     {isExpanded && (
                                     <div className="p-4 space-y-4 animate-in slide-in-from-top-2 duration-300">
                                         <div className="grid grid-cols-2 gap-y-4 gap-x-6">
                                             <div className="space-y-0.5">
                                                 <span className="text-[10px] text-gray-400 uppercase font-bold tracking-tighter block">Date & time</span>
                                                 <div className="text-xs font-bold text-gray-800">
                                                     {formatDate(round.scheduledAt, { includeTime: false })} · {formatTime(round.scheduledAt)}
                                                 </div>
                                             </div>
                                             <div className="space-y-0.5">
                                                 <span className="text-[10px] text-gray-400 uppercase font-bold tracking-tighter block">Duration</span>
                                                 <div className="text-xs font-bold text-gray-800">{round.duration} minutes</div>
                                             </div>
                                             <div className="space-y-0.5">
                                                 <span className="text-[10px] text-gray-400 uppercase font-bold tracking-tighter block">Type</span>
                                                 <div className="text-xs font-bold text-gray-800 capitalize flex items-center gap-1.5">
                                                     {round.interviewType === 'video' ? <Video className="h-3 w-3" /> : <MapPin className="h-3 w-3" />}
                                                     {round.interviewType?.replace('_', ' ')}
                                                 </div>
                                             </div>
                                             <div className="space-y-0.5">
                                                 <span className="text-[10px] text-gray-400 uppercase font-bold tracking-tighter block">Interviewer</span>
                                                 <div className="text-xs font-bold text-gray-800">{round.interviewer?.name}</div>
                                             </div>
                                         </div>

                                         {round.interviewType === 'in_person' && round.location && (
                                             <div className="space-y-1">
                                                 <span className="text-[10px] text-gray-400 uppercase font-bold tracking-tighter block">Office Address</span>
                                                 <div className="p-3 bg-gray-50 rounded-lg border border-gray-100 text-[11px] text-gray-700 flex items-start gap-2.5 font-medium leading-relaxed">
                                                     <MapPin className="h-4 w-4 text-brand flex-shrink-0 mt-0.5" />
                                                     <span>{round.location}</span>
                                                 </div>
                                             </div>
                                         )}

                                         {round.meetingLink && (
                                             <div className="space-y-1">
                                                 <span className="text-[10px] text-gray-400 uppercase font-bold tracking-tighter block">Meeting link</span>
                                                 <a href={round.meetingLink} target="_blank" rel="noopener noreferrer" className="text-[#0F2942] text-[11px] font-medium underline flex items-center gap-1 break-all">
                                                     {round.meetingLink.replace('https://', '').replace('http://', '')} <ExternalLink className="h-2.5 w-2.5" />
                                                 </a>
                                             </div>
                                         )}

                                         {round.notes && (
                                             <div className="space-y-1">
                                                 <span className="text-[10px] text-gray-400 uppercase font-bold tracking-tighter block">Instructions / Notes</span>
                                                 <div className="p-3 bg-gray-50 rounded-lg border border-gray-100 text-xs text-gray-600 leading-relaxed font-medium">
                                                     {round.notes}
                                                 </div>
                                             </div>
                                         )}

                                         {/* Per-round assessment box */}
                                         <div className="pt-4 mt-2 border-t border-gray-50">
                                             {(round.status === 'Scheduled' || round.status === 'In Progress') && canHandleRound ? (
                                                 <div className="space-y-3">
                                                     <div className="flex items-center gap-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                                                         <AlertCircle className="h-3 w-3 text-brand" />
                                                         Assessment update
                                                     </div>

                                                     {actingRoundId === round.id ? (
                                                         <div className="space-y-3 animate-in slide-in-from-top-1 duration-200">
                                                             <textarea
                                                                 placeholder={pendingStatus === 'Completed' ? "Add final assessment notes and feedback..." : "Provide a reason for cancellation..."}
                                                                 className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-xs focus:bg-white focus:ring-1 focus:ring-brand outline-none transition-all"
                                                                 rows="3"
                                                                 value={internalNotes}
                                                                 onChange={(e) => setInternalNotes(e.target.value)}
                                                                 autoFocus
                                                             />
                                                             <div className="flex gap-2">
                                                                 <button
                                                                     className="flex-1 py-2 text-[11px] font-bold text-gray-500 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
                                                                     onClick={() => {
                                                                         setActingRoundId(null);
                                                                         setPendingStatus(null);
                                                                         setInternalNotes('');
                                                                     }}
                                                                 >
                                                                     Back
                                                                 </button>
                                                                 <button
                                                                     className={`flex-[2] py-2 text-[11px] font-bold text-white rounded-lg shadow-sm ${pendingStatus === 'Completed' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}
                                                                     onClick={() => updateStatusMutation.mutate({
                                                                         status: pendingStatus,
                                                                         additionalData: { roundId: round.id, notes: internalNotes }
                                                                     })}
                                                                     disabled={updateStatusMutation.isPending}
                                                                 >
                                                                     {updateStatusMutation.isPending ? 'Processing...' : `Confirm ${pendingStatus === 'Completed' ? 'Completed' : 'Cancellation'}`}
                                                                 </button>
                                                             </div>
                                                         </div>
                                                     ) : (
                                                         <div className="flex gap-2">
                                                             <button
                                                                 className="flex-1 py-2 px-3 bg-green-50 text-green-700 border border-green-100 rounded-lg text-xs font-bold hover:bg-green-600 hover:text-white transition-all flex items-center justify-center gap-1.5"
                                                                 onClick={() => {
                                                                     setActingRoundId(round.id);
                                                                     setPendingStatus('Completed');
                                                                 }}
                                                             >
                                                                 <CheckCircle className="h-3.5 w-3.5" /> Mark as done
                                                             </button>
                                                             <button
                                                                 className="flex-1 py-2 px-3 bg-red-50 text-red-700 border border-red-100 rounded-lg text-xs font-bold hover:bg-red-600 hover:text-white transition-all flex items-center justify-center gap-1.5"
                                                                 onClick={() => {
                                                                     setActingRoundId(round.id);
                                                                     setPendingStatus('Cancelled');
                                                                 }}
                                                             >
                                                                 <XCircle className="h-3.5 w-3.5" /> Cancel round
                                                             </button>
                                                         </div>
                                                     )}

                                                 </div>
                                             ) : round.completionRemark?.remark ? (
                                                 <div className="space-y-2">
                                                     <div className={`text-[11px] font-bold p-2.5 rounded-lg border flex items-center gap-2 ${round.status === 'Completed' ? 'bg-green-50 text-green-800 border-green-100' : 'bg-red-50 text-red-800 border-red-100'}`}>
                                                         {round.status === 'Completed' ? <CheckCircle className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
                                                         Round marked as <strong>{round.status}</strong>
                                                     </div>
                                                     <div className="p-3 bg-white italic text-xs text-gray-700 border border-gray-100 rounded-lg leading-relaxed">
                                                         "{round.completionRemark.remark}"
                                                         <div className="mt-2 pt-2 border-t border-gray-50 flex items-center justify-between text-[10px] non-italic not-italic font-bold text-gray-400">
                                                             <span className="capitalize">Processed by: {round.completionRemark.remarkerName || "interviewer"}</span>
                                                             <span>{formatDate(round.completionRemark.remarkedAt)}</span>
                                                         </div>
                                                     </div>
                                                 </div>
                                             ) : (
                                                 <div className="text-xs font-bold text-gray-400 italic text-center py-2 flex items-center justify-center gap-2">
                                                     <Info className="h-3.5 w-3.5" />
                                                     {['Scheduled', 'In Progress'].includes(round.status) ? "Assessment evaluation pending" : "Assessment details already finalized"}
                                                 </div>
                                             )}
                                         </div>
                                     </div>
                                     )}
                                </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Right Column: Record Info & Links */}
                <div className="space-y-6">

                    {/* Meeting Links Quick Access */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                        <div className="bg-gray-50 px-5 py-3 border-b border-gray-100 flex items-center gap-2 text-sm font-bold text-gray-800">
                            <LinkIcon className="h-4 w-4 text-brand" />
                            <span>Meeting links</span>
                        </div>
                        <div className="p-5 space-y-4">
                            {interview.rounds?.filter(r => r.meetingLink).length > 0 ? (
                                interview.rounds.filter(r => r.meetingLink).map((round, i) => (
                                    <div key={i} className="space-y-2">
                                        <div className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">
                                            Round {interview.rounds.indexOf(round) + 1} — {round.roundType} — {round.interviewer?.name}
                                        </div>
                                        <a
                                            href={round.meetingLink}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="block w-full py-2.5 px-4 bg-blue-50 text-[#0B2033] border border-blue-100 rounded-lg text-xs font-bold hover:bg-[#0F2942] hover:text-white transition-all text-center"
                                        >
                                            Join meeting &#8599;
                                        </a>
                                        {i < interview.rounds.filter(r => r.meetingLink).length - 1 && <hr className="border-gray-50 mt-4" />}
                                    </div>
                                ))
                            ) : (
                                <div className="text-center py-4">
                                    <Video className="h-8 w-8 text-gray-200 mx-auto mb-2" />
                                    <p className="text-xs text-gray-400 italic">No valid meeting links found</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Metadata Section */}
                    <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 space-y-4">
                        <div className="text-[11px] font-bold text-gray-400 uppercase tracking-widest border-b border-gray-200 pb-2 mb-2">Record info</div>

                        <div className="space-y-1">
                            <span className="text-[10px] text-gray-400 uppercase font-bold tracking-widest block">Scheduled by</span>
                            <div className="flex items-center gap-2">
                                <div className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center text-[10px] text-gray-600 font-bold uppercase border border-gray-300">
                                    {interview.createdByName?.charAt(0) || "S"}
                                </div>
                                <span className="text-xs font-bold text-gray-700 uppercase">{interview.createdByName || 'Super Admin'}</span>
                            </div>
                        </div>

                        <div className="space-y-1">
                            <span className="text-[10px] text-gray-400 uppercase font-bold tracking-widest block">Created at</span>
                            <span className="text-xs font-bold text-gray-700">{formatDate(interview.createdAt)}</span>
                        </div>

                        <div className="space-y-1">
                            <span className="text-[10px] text-gray-400 uppercase font-bold tracking-widest block">Last updated</span>
                            <span className="text-xs font-bold text-gray-700">{formatDate(interview.updatedAt)}</span>
                        </div>

                        <div className="space-y-1 pt-2 border-t border-gray-200">
                            <span className="text-[10px] text-gray-400 uppercase font-bold tracking-widest block">Interview ID</span>
                            <span className="text-[11px] font-bold text-gray-500 font-mono tracking-tighter">{interview.interviewId}</span>
                        </div>
                    </div>

                </div>
            </div>

            {/* Edit Modal */}
            <ScheduleInterviewModal
                isOpen={showScheduleForm}
                onClose={() => setShowScheduleForm(false)}
                onSuccess={() => {
                    // Refetch interview details after edit
                    const fetchInterview = async () => {
                        const response = await interviewService.getInterviewById(id);
                        if (response.responseCode === 200 && response.data) {
                            setInterview(response.data);
                        }
                    };
                    fetchInterview();
                    setShowScheduleForm(false);
                }}
                editData={editInterviewData}
            />
        </div>
    );
};

export default ViewInterview;
